// src/utils/tensionMapper.js
// Maps 8-dimension scores into 3 shareable "tensions" for the presentation layer.
// Each tension is a spectrum from -100 (left pole) to +100 (right pole).

/**
 * Normalize a raw difference to a -100..+100 range.
 * @param {number} value - Raw difference
 * @param {number} maxMagnitude - Maximum possible magnitude of the raw difference
 * @returns {number} Normalized value from -100 to +100
 */
function normalize(value, maxMagnitude) {
  if (maxMagnitude === 0) return 0;
  return Math.round(
    Math.max(-100, Math.min(100, (value / maxMagnitude) * 100))
  );
}

/**
 * Compute 3 core tensions from 8-dimension scores.
 * @param {Object} scores - 8-dimension scores, each 0-100
 * @returns {Object} Three tension objects with value, labels, and descriptions
 */
export function computeTensions(scores) {
  const care = scores.CARE_EMPATHY || 0;
  const achievement = scores.ACHIEVEMENT_PRAGMATISM || 0;
  const tradition = scores.TRADITION_LOYALTY || 0;
  const security = scores.SECURITY_AUTHORITY || 0;
  const progress = scores.PROGRESS_OPENNESS || 0;
  const autonomy = scores.AUTONOMY_LIBERTY || 0;
  const sanctity = scores.SANCTITY_PURITY || 0;
  const fairness = scores.FAIRNESS_EQUITY || 0;

  // Heart vs. Head: CARE_EMPATHY vs. ACHIEVEMENT_PRAGMATISM
  const heartVsHeadRaw = care - achievement;
  const heartVsHeadValue = normalize(heartVsHeadRaw, 100);

  // Roots vs. Wings: (TRADITION + SECURITY) vs. (PROGRESS + AUTONOMY)
  const rootsVsWingsRaw = tradition + security - (progress + autonomy);
  const rootsVsWingsValue = normalize(rootsVsWingsRaw, 200);

  // Rules vs. Context: (SECURITY + SANCTITY) vs. (FAIRNESS + AUTONOMY)
  const rulesVsContextRaw = security + sanctity - (fairness + autonomy);
  const rulesVsContextValue = normalize(rulesVsContextRaw, 200);

  return {
    heartVsHead: {
      value: heartVsHeadValue,
      leftLabel: 'Head',
      rightLabel: 'Heart',
      label:
        heartVsHeadValue > 15
          ? 'Heart-led'
          : heartVsHeadValue < -15
            ? 'Head-led'
            : 'Balanced',
      description:
        heartVsHeadValue > 30
          ? 'You lead with empathy. Decisions start with "who does this affect?" before "what makes sense?"'
          : heartVsHeadValue < -30
            ? 'You lead with logic. Decisions start with "what works?" before "how does this feel?"'
            : 'You balance emotion and pragmatism, switching between them depending on what the situation demands.',
    },
    rootsVsWings: {
      value: rootsVsWingsValue,
      leftLabel: 'Wings',
      rightLabel: 'Roots',
      label:
        rootsVsWingsValue > 15
          ? 'Rooted'
          : rootsVsWingsValue < -15
            ? 'Free-flying'
            : 'Balanced',
      description:
        rootsVsWingsValue > 30
          ? 'You value stability, tradition, and proven paths. Change needs to earn your trust.'
          : rootsVsWingsValue < -30
            ? 'You value freedom, innovation, and new possibilities. Staying still feels like going backward.'
            : 'You honor the past while staying open to the future. You change when it makes sense, not for its own sake.',
    },
    rulesVsContext: {
      value: rulesVsContextValue,
      leftLabel: 'Context',
      rightLabel: 'Rules',
      label:
        rulesVsContextValue > 15
          ? 'Rules-first'
          : rulesVsContextValue < -15
            ? 'Context-first'
            : 'Balanced',
      description:
        rulesVsContextValue > 30
          ? 'You believe consistent principles matter more than situational exceptions. Rules exist for a reason.'
          : rulesVsContextValue < -30
            ? 'You believe every situation is unique. Rigid rules often cause more harm than the problems they solve.'
            : "You respect rules but evaluate them against the situation. Principles guide you, but they don't bind you.",
    },
  };
}

export default computeTensions;
